/**
 * Row actions in lists: a run, task, decision or landing row shows its top registry verbs with
 * keycaps on hover, focus and selection, and the row's key handler runs any of its verbs while the
 * row has focus, without opening the inspector. One `ActionCtx` is built per list by
 * `RowActionsProvider`; rows call `useRowActions(entity)`. A focused row also publishes its verbs
 * as the current selection, so the status line and ⌘K show the same keys.
 */
import { createContext, type KeyboardEvent, type ReactNode, useContext, useEffect } from 'react'
import type { Action, ActionCtx, ActionEntity } from '../lib/actions.js'
import { useActionCtx, type VerbHandlers } from '../lib/entity-actions.js'
import { rowActionForKey, rowActionList, topRowActions } from '../lib/row-actions.js'
import { setSelection } from '../lib/selection-actions.js'
import { isTyping } from '../lib/shortcut.js'

const Ctx = createContext<ActionCtx | null>(null)

export function RowActionsProvider(p: {
  projectId?: string | undefined
  handlers?: VerbHandlers
  children: ReactNode
}) {
  const ctx = useActionCtx({
    ...(p.projectId ? { projectId: p.projectId } : {}),
    ...(p.handlers ? { handlers: p.handlers } : {}),
  })
  return <Ctx.Provider value={ctx}>{p.children}</Ctx.Provider>
}

export interface RowActionsApi {
  list: Action[]
  top: Action[]
  /** Spread on the row element: key handler, selection publishing and the hover/focus host. */
  rowProps: {
    'data-row-host': true
    onKeyDown: (e: KeyboardEvent<HTMLElement>) => void
    onFocus: () => void
    onBlur: (e: React.FocusEvent<HTMLElement>) => void
  }
}

/** Keys the triage lists own themselves (`s` accept, `r` reply, `d` decide for me, `e` later). */
export const TRIAGE_KEYS: ReadonlySet<string> = new Set(['s', 'r', 'd', 'e', 'x', 'j', 'k'])

export function useRowActions(
  entity: ActionEntity | undefined,
  o: { skipKeys?: ReadonlySet<string>; max?: number } = {},
): RowActionsApi {
  const ctx = useContext(Ctx)
  const list = ctx ? rowActionList(entity, ctx, o.skipKeys ? { skipKeys: o.skipKeys } : {}) : []
  const top = topRowActions(list, o.max)
  const owner = `row:${entity?.kind}:${entity?.id}`
  const title = entity?.title ?? ''
  // A row that unmounts while focused must not leave its verbs behind.
  useEffect(() => () => setSelection(owner, undefined), [owner])
  return {
    list,
    top,
    rowProps: {
      'data-row-host': true,
      onKeyDown: (e) => {
        if (e.defaultPrevented || !list.length) return
        if (isTyping(e.nativeEvent)) return
        if (document.querySelector('[role="dialog"][aria-modal="true"]')) return
        const a = rowActionForKey(list, e.nativeEvent)
        if (!a) return
        e.preventDefault()
        e.stopPropagation()
        a.run()
      },
      onFocus: () => {
        if (list.length) setSelection(owner, { title, actions: list })
      },
      onBlur: (e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null))
          setSelection(owner, undefined)
      },
    },
  }
}

/** The verbs as buttons with visible keycaps; shown on hover, focus and selection (CSS). */
export function RowActionBar({ api, label }: { api: RowActionsApi; label: string }) {
  if (!api.top.length) return null
  return (
    <span className="row-acts" role="group" aria-label={`Actions for ${label}`}>
      {api.top.map((a) => (
        <button
          key={a.id}
          type="button"
          className={`row-act${a.danger ? ' is-danger' : ''}`}
          aria-keyshortcuts={a.key.replace('⇧', 'Shift+')}
          title={a.vikoCommand ?? a.label}
          onClick={(e) => {
            e.stopPropagation()
            a.run()
          }}
        >
          <span>{a.label}</span>
          <kbd className="row-act__key">{a.key}</kbd>
        </button>
      ))}
    </span>
  )
}
